/* eslint-disable no-underscore-dangle */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-return-await */
/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Group, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { Content, Editor, EditorProvider, Extensions } from '@tiptap/react';
import { CSSProperties, FC, ReactNode, useEffect, useState } from 'react';
import { i18n } from '@lingui/core';
import Paragraph from '@tiptap/extension-paragraph';
import { Editor as RteEditor } from '@tiptap/core';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { IconQuestionMark } from '@tabler/icons';
import {
  replacePlaceholdersHtmlWithPlaceholdersId,
  replacePlaceholdersWithHtmlSettings,
} from '../../Service/Placeholder/PlaceholderResover';
import { TiptapS } from '../../Service/TiptapS';
import useRteStyles from '../../Service/hooks/useRteStyles';
import {
  EventPlaceholderExtension,
  SeqNumberPlaceholderExtension,
  SettingsPlaceholderExtension,
} from './placeholder/PlaceholderExtensions';
import { EmailInputBreakExtension } from './EmailInputBreakExtension';
import { HardBreakExtension } from './HardBreakExtension';
import { FileExtensions } from './ImageExtensions';

interface Props {
  value: string;
  label?: ReactNode;
  description?: string;
  patchField?: (value: string) => Promise<boolean>;
  onUpdate?: (html: string) => void;
  toolbar?: ReactNode;
  menu?: ReactNode;
  isEmailInput?: boolean;
  setEditor?: (editor: Editor) => unknown;
  textAlign?: string;
  omitParagraphWrapper?: boolean;
  styles?: CSSProperties;
  includeListExtentions?: boolean;
  includeImages?: boolean;
  disabled?: boolean;
  eventScope?: boolean;
  extensions?: Extensions;
  updateTrigger?: number;
}

export const SequentialNumberInput: FC<Props> = ({ value, patchField, onUpdate, ...rest }) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);

  const preChange = (html: string) =>
    TiptapS.removeParagraphWrapper(replacePlaceholdersHtmlWithPlaceholdersId(html))
      .replace(' ', '')
      .replace(/&nbsp;/g, ' ');

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  const _onUpdate = (html: string) => {
    if (onUpdate) {
      onUpdate(preChange(html));
    }
  };

  return (
    <TiptapInput
      baseInput
      extensions={[...TiptapS.Extentions.MINIMAL, SeqNumberPlaceholderExtension]}
      content={content}
      onChange={patchField ? onChange : undefined}
      onUpdate={onUpdate ? _onUpdate : undefined}
      {...rest}
    />
  );
};

export const EventBaseInput: FC<Props> = ({ ...rest }) => <BaseInput {...rest} eventScope />;

export const BaseInput: FC<Props> = ({ value, patchField, onUpdate, eventScope = false, extensions, ...rest }) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);
  const preChange = (html: string) => TiptapS.removeParagraphWrapper(replacePlaceholdersHtmlWithPlaceholdersId(html));

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  const _onUpdate = (html: string) => {
    if (onUpdate) {
      onUpdate(preChange(html));
    }
  };

  const finalExtensions = extensions ?? TiptapS.Extentions.MINIMAL;

  return (
    <TiptapInput
      baseInput
      extensions={[...finalExtensions, eventScope ? EventPlaceholderExtension : SettingsPlaceholderExtension]}
      content={content}
      onChange={patchField ? onChange : undefined}
      onUpdate={onUpdate ? _onUpdate : undefined}
      {...rest}
    />
  );
};

export const RichInputWithoutMentions: FC<Props> = ({ value, patchField, ...rest }) => (
  <TiptapInput extensions={TiptapS.Extentions.EXTENDED} content={value} onChange={patchField} {...rest} />
);

export const EventRichInput: FC<Props> = ({ ...rest }) => <RichInput {...rest} eventScope />;

export const RichInput: FC<Props> = ({
  value,
  patchField,
  onUpdate,
  omitParagraphWrapper = true,
  eventScope = false,
  ...rest
}) => {
  const content = replacePlaceholdersWithHtmlSettings(i18n, value);

  const preChange = (html: string) => {
    const tmp = replacePlaceholdersHtmlWithPlaceholdersId(html);
    if (omitParagraphWrapper) {
      return TiptapS.removeParagraphWrapper(tmp);
    }
    return tmp;
  };

  const onChange = async (html: string) => {
    if (patchField) {
      return patchField(preChange(html));
    }
    return Promise.resolve(true);
  };

  const _onUpdate = (html: string) => {
    if (onUpdate) {
      onUpdate(preChange(html));
    }
  };

  return (
    <TiptapInput
      extensions={[
        ...TiptapS.Extentions.EXTENDED,
        eventScope ? EventPlaceholderExtension : SettingsPlaceholderExtension,
      ]}
      content={content}
      onChange={onChange}
      onUpdate={onUpdate ? _onUpdate : undefined}
      {...rest}
    />
  );
};

interface TiptapProps {
  extensions: Extensions;
  content: Content;
  onChange?: (html: string) => Promise<boolean>;
  onUpdate?: (html: string) => void;
  toolbar?: ReactNode;
  menu?: ReactNode;
  label?: ReactNode;
  description?: string;
  isEmailInput?: boolean;
  setEditor?: (editor: Editor) => unknown;
  textAlign?: string;
  styles?: CSSProperties;
  includeListExtentions?: boolean;
  includeImages?: boolean;
  baseInput?: boolean;
  disabled?: boolean;
  updateTrigger?: number;
}

const TiptapInput: FC<TiptapProps> = ({
  extensions,
  content,
  onChange,
  onUpdate,
  label,
  description,
  toolbar,
  menu,
  textAlign,
  isEmailInput = false,
  setEditor,
  styles,
  includeListExtentions = false,
  includeImages = false,
  baseInput = false,
  disabled = false,
  updateTrigger,
}) => {
  const [focused, setFocused] = useState(false);
  const [showError, setShowError] = useState(false);
  const [rte, setRte] = useState<RteEditor>();
  const [isDirty, setDirty] = useState(false);
  const { classes, cx } = useRteStyles({ textAlign, styles, focused, isEmailInput }, { name: 'TiptapEditor' });

  useEffect(() => {
    if (updateTrigger && rte) {
      // Value was updated from an outside source. E.g: Template was selected
      rte.commands.setContent(content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTrigger, rte]);

  // Increase row height for email related inputs
  if (isEmailInput) {
    extensions.push(
      Paragraph.configure({
        HTMLAttributes: {
          style: 'margin-bottom: 1.5em;',
        },
      }),
      EmailInputBreakExtension,
    );
  }
  // Use <br> instead of <p> for new line when list extensions are not present
  // Don't allow new lines on one-line input (email subjects)
  if (!isEmailInput && !includeListExtentions && !baseInput) {
    extensions.push(HardBreakExtension);
  }

  if (isEmailInput || includeListExtentions) {
    extensions.push(OrderedList, BulletList);
  }

  if (includeImages) {
    extensions.push(...FileExtensions);
  }

  // eslint-disable-next-line no-underscore-dangle
  const _onChange = async (value: string) => {
    if (onChange) {
      const success = await onChange(value);
      if (!success && !showError) {
        setShowError(true);
      }
      if (success) {
        setDirty(false);
        if (showError) {
          setShowError(false);
        }
      }
    }
  };

  return (
    <>
      {label && (
        <Group spacing={5}>
          <Text size={12} sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : theme.colors.gray[9] })}>
            {label}
          </Text>
          {description && (
            <Tooltip
              multiline
              withArrow
              position="bottom"
              label={i18n._(description)}
              width={200}
              transition="slide-down"
            >
              <ThemeIcon
                size={14}
                sx={(theme) => ({
                  borderRadius: '50%',
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.silver[4] : theme.colors.gray[3],
                })}
              >
                <IconQuestionMark size={12} />
              </ThemeIcon>
            </Tooltip>
          )}
        </Group>
      )}
      <Box className={cx(classes.root)}>
        <EditorProvider
          injectCSS={false}
          extensions={extensions}
          content={content}
          editorProps={{
            transformPastedHTML: TiptapS.stripHtmlStylingOnPaste,
          }}
          slotAfter={toolbar}
          onFocus={() => setFocused(true)}
          editable={!disabled}
          onBlur={({ editor }) => {
            setFocused(false);
            if (onChange && isDirty) {
              _onChange(editor.getHTML());
            }
          }}
          onUpdate={({ editor }) => {
            setDirty(true);
            if (onUpdate) {
              onUpdate(editor.getHTML());
            }
          }}
          onCreate={({ editor }) => {
            setRte(editor);
            if (setEditor) {
              setEditor(editor as Editor);
            }
          }}
        >
          {menu}
        </EditorProvider>
      </Box>
      {showError && (
        <Text color="red" size="xs">
          {i18n._('error.input-field')}
        </Text>
      )}
    </>
  );
};
